import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import loyaltyService from '../../services/loyaltyService';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import WalletOverview from './components/WalletOverview';
import TransactionHistory from './components/TransactionHistory';
import RewardsCatalog from './components/RewardsCatalog';
import EarningOpportunities from './components/EarningOpportunities';
import RedemptionHistory from './components/RedemptionHistory';

const LoyaltyWallet = () => {
  const { user, userProfile, loading: authLoading } = useAuth();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [walletData, setWalletData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load wallet data
  useEffect(() => {
    const loadWalletData = async () => {
      if (!user?.id) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const { data, error } = await loyaltyService?.getUserWallet(user?.id);
        
        if (error) {
          setError(error?.message || 'Failed to load wallet data');
        } else {
          setWalletData(data);
        }
      } catch (err) {
        setError('Failed to connect to loyalty service');
      } finally {
        setLoading(false);
      }
    };

    if (!authLoading && user) {
      loadWalletData();
    }
  }, [user, authLoading]);

  // Refresh wallet data
  const refreshWallet = async () => {
    if (!user?.id) return;
    
    try {
      const { data, error } = await loyaltyService?.getUserWallet(user?.id);
      
      if (error) {
        setError(error?.message || 'Failed to refresh wallet');
      } else {
        setWalletData(data);
        setError(null);
      }
    } catch (err) {
      setError('Failed to refresh wallet data');
    }
  };

  // Handle token earning (this would be called from activity completion)
  const handleTokensEarned = (amount, description) => {
    if (walletData) {
      setWalletData({
        ...walletData,
        current_balance: walletData?.current_balance + amount,
        lifetime_earned: walletData?.lifetime_earned + amount
      });
    }
  };

  // Tab navigation
  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'Wallet' },
    { id: 'earn', label: 'Earn Tokens', icon: 'Plus' },
    { id: 'rewards', label: 'Rewards', icon: 'Gift' },
    { id: 'history', label: 'History', icon: 'Clock' },
    { id: 'redemptions', label: 'My Redemptions', icon: 'ShoppingBag' }
  ];

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span className="text-muted-foreground">Loading your wallet...</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Lock" size={48} className="mx-auto mb-4 text-muted-foreground" />
          <h2 className="text-xl font-semibold text-foreground mb-2">Authentication Required</h2>
          <p className="text-muted-foreground">Please sign in to view your loyalty wallet.</p>
        </div>
      </div>
    );
  }

  const mockUser = {
    id: user?.id || 1,
    name: userProfile?.full_name || user?.email?.split('@')?.[0] || "User",
    email: user?.email || "user@example.com",
    tier: walletData?.loyalty_tier || "Member",
    avatar: userProfile?.avatar_url
  };

  const mockNotifications = [
    {
      id: 1,
      title: "Tokens Earned!",
      message: "You earned 10 tokens for completing your morning yoga session.",
      category: "tokens",
      time: "1 hour ago",
      read: false
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header 
        user={mockUser}
        notifications={mockNotifications}
        onNotificationClick={(notification) => console.log('Notification:', notification)}
        onProfileClick={() => window.location.href = '/member-profile'}
      />
      <div className="flex">
        <Sidebar 
          isCollapsed={isSidebarCollapsed}
          onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          user={mockUser}
        />
        
        <main className={`flex-1 transition-all duration-300 ${
          isSidebarCollapsed ? 'ml-16' : 'ml-64'
        } p-6`}>
          {/* Header Section */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
                  Loyalty Wallet
                </h1>
                <p className="text-muted-foreground">
                  Earn tokens through wellness activities and redeem them for exclusive rewards
                </p>
              </div>
              
              <div className="flex items-center space-x-4">
                <button
                  onClick={refreshWallet}
                  className="p-2 rounded-lg border border-border bg-card hover:bg-muted/10 transition-colors"
                  title="Refresh wallet data"
                >
                  <Icon name="RefreshCw" size={20} className="text-muted-foreground" />
                </button>
                
                {walletData && (
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">Available Balance</p>
                    <p className="text-2xl font-bold text-primary">
                      {loyaltyService?.formatTokens(walletData?.current_balance)} tokens
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Error Display */}
            {error && (
              <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center space-x-3">
                <Icon name="AlertTriangle" size={20} className="text-destructive flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-destructive">Error loading wallet</p>
                  <p className="text-sm text-destructive/80">{error}</p>
                </div>
                <button
                  onClick={refreshWallet}
                  className="ml-auto px-3 py-1 bg-destructive text-destructive-foreground rounded text-sm hover:bg-destructive/90 transition-colors"
                >
                  Retry
                </button>
              </div>
            )}

            {/* Tab Navigation */}
            <div className="border-b border-border">
              <nav className="flex space-x-8">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === tab?.id
                        ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                    }`}
                  >
                    <Icon name={tab?.icon} size={18} />
                    <span>{tab?.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          <div className="space-y-6">
            {activeTab === 'overview' && (
              <WalletOverview 
                walletData={walletData} 
                user={mockUser}
                onRefresh={refreshWallet}
              />
            )}
            
            {activeTab === 'earn' && (
              <EarningOpportunities 
                user={mockUser}
                onTokensEarned={handleTokensEarned}
              />
            )}
            
            {activeTab === 'rewards' && (
              <RewardsCatalog 
                userBalance={walletData?.current_balance || 0}
                onRedemption={refreshWallet}
              />
            )}
            
            {activeTab === 'history' && (
              <TransactionHistory 
                userId={user?.id} 
              />
            )}
            
            {activeTab === 'redemptions' && (
              <RedemptionHistory 
                userId={user?.id} 
              />
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default LoyaltyWallet;